import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BootstrapDialogLinkType, UtilityService } from '@eforall/common';
import { buildColumn, DashboardDialogFramePart, DialogAction, DialogContext, GridAction, GridColumn, GridContentWidget, GridSetup } from '@eforall/dashboard';
import { AlgoliaObjectType, AlgoliaSearchObject } from '@interfaces';
import { AlgoliaSearchPart } from './algolia-search/algolia-search.part';

export interface SearchDialogData {
	searchOnlyForPerson: boolean,
	objectTypes?: AlgoliaObjectType[], // If provided, searches only the specified AlgoliaObjectType values
}

@Component({
	selector: 'search-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		AlgoliaSearchPart,
		GridContentWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './search.dialog.html'
})

export class SearchDialog {

	public dialogRef = inject(MatDialogRef<SearchDialog>);
	public dialogContext: DialogContext<SearchDialogData> = inject(MAT_DIALOG_DATA);

	private util = inject(UtilityService);
	public algoliaSearchHits: AlgoliaSearchObject[] = [];
	private rowSelected = signal<AlgoliaSearchObject | undefined>(undefined);

	// subtracting 70 to account for padding and tabstrip
	public customHeight = computed(() => this.dialogContext.dimensions().bodyHeight - 70);


	constructor() {
	}

	public actions = computed<DialogAction[]>(() => {
		const buttons: DialogAction[] = [];
		if (this.dialogContext.data?.searchOnlyForPerson) {
			buttons.push(this.setDialogActionData('select', !!this.rowSelected(), 'Select', 'no-action', this.rowSelected()));
		} else {
			buttons.push(this.setDialogActionData('explore', !!this.rowSelected(), 'Explore', 'no-action', this.rowSelected()));
		}
		buttons.push(this.setDialogActionData('cancel', true, 'Cancel', 'no-action'));
		return buttons;
	});

	private gridActions = computed<GridAction<AlgoliaSearchObject>[]>(() => {
		const actions: GridAction<AlgoliaSearchObject>[] = [];
		if (this.dialogContext.data?.searchOnlyForPerson) {
			actions.push({
				iconClass: 'fa-regular fa-hand-pointer',
				label: 'Select',
				isEnabled: rows => !!rows.length,
				execute: async rows => {
					this.dialogRef.close(this.setDialogActionData('search', true, 'Search', 'no-action', rows[0]));
				},
			});
		}

		actions.push({
			iconClass: 'fa-regular fa-pencil',
			label: 'Explore',
			isEnabled: rows => !!rows.length,
			execute: async rows => {
				this.dialogRef.close(this.setDialogActionData('explore', true, 'Explore', 'no-action', rows[0]));
			},
		})
		return actions;
	});

	private gridColumns: GridColumn<AlgoliaSearchObject, unknown>[] = [
		buildColumn(this.util, 'Text', row => row.objectType, { header: 'Type' }),
		buildColumn(this.util, 'Number', row => row.id, { header: 'ID' }),
		buildColumn(this.util, 'Text', row => row.displayName, { header: 'Name' }),
		buildColumn(this.util, 'Text', row => row.addressObj?.cityAndState, { header: 'Location' }),
		buildColumn(this.util, 'Text', row => row.trimesterName, { header: 'Trimester' }),
	];

	public setup = computed<GridSetup<AlgoliaSearchObject>>(() => {
		return {
			rowName: { singular: 'Search Result', plural: 'Search Results' },
			getRowKey: row => '' + row.objectID,
			stateKey: 'search-grid',
			actions: this.gridActions(),
			buttons: [],
			columns: this.gridColumns,
		}
	});

	private setDialogActionData(id: string, enabled: boolean, text: string, linkType: BootstrapDialogLinkType, callbackResult?: AlgoliaSearchObject): DialogAction<AlgoliaSearchObject> {
		return {
			id,
			enabled: signal(enabled),
			visible: signal(true),
			text: signal(text),
			cssClass: signal('btn-standard'),
			linkType,
			willCloseDialog: true,
			callbackResult,
		}
	};

	getSelectedRows($event: AlgoliaSearchObject[]) {
		this.rowSelected.set($event[0]);
	};
}

